import { useEffect, useRef, useState } from 'react'
import { Channel as StreamChannel, StreamChat } from 'stream-chat'
import { App } from '@capacitor/app'
import { Keyboard } from '@capacitor/keyboard'
import { PluginListenerHandle } from '@capacitor/core'
import Bugsnag from '@bugsnag/js'
import api from '../../services/api.services'
import { UserId } from '../../services/api.types'
import { getChatClient, channelType, clearChannelPushNotifications } from '../../views/ChallengeChat/hooks/useInitChat'
import { isNative } from '../../common/helpers/isNative'
import { getUserCompanies } from '../../views/RegisterUser/store/RegisterUserSelectors'
import { IGlobalState } from '../../Root/store/appReducer'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'
import { store } from '../../index'

export type ChatChannelKind = 'challenge' | 'group'
export const groupChannelType = 'group'

// Opens a single chat channel of either kind on the shared singleton client.
// Errors never redirect — hosts embed this and render an inline error state instead.
export const useChatChannel = (userId: UserId, name: string, kind?: ChatChannelKind, id?: string) => {
  const [chatClient, setChatClient] = useState<StreamChat>()
  const [currentChannel, setCurrentChannel] = useState<StreamChannel>()
  const [error, setError] = useState(false)
  const requestIdRef = useRef(0)

  useEffect(() => {
    chatClient?.setMessageComposerSetupFunction(({ composer }) => {
      const userCompanies = getUserCompanies(store.getState() as any as IGlobalState)
      const allowAllFiles = hasFeatureFlag(userCompanies, 'chatAllUpload')
      const allowVideoFiles = hasFeatureFlag(userCompanies, 'chatVideoUpload')
      composer.updateConfig({
        attachments: {
          acceptedFiles: allowAllFiles ? [] : allowVideoFiles ? ['image/*', 'video/*'] : ['image/*'],
        },
        location: {
          enabled: false,
        },
      })
    })
  }, [chatClient])

  useEffect(() => {
    if (!isNative()) return
    const listeners: PluginListenerHandle[] = []
    let removed = false

    // The chat sizes itself with 100vh-based calcs, and 100vh does not shrink when the native
    // keyboard opens. Instead of modelling the keyboard/chrome geometry, measure it: size the
    // chat so it ends above the visual viewport's bottom (which iOS keeps above the keyboard)
    // by the same gap the closed-keyboard layout leaves between the input and the bottom nav.
    const chatEl = () => document.querySelector('.yum-chat-view .messaging.str-chat') as HTMLElement | null
    let closedGap: number | undefined
    const fitChatAboveKeyboard = (keyboardHeight: number) => {
      const el = chatEl()
      if (!el) return
      const rect = el.getBoundingClientRect()
      // Measure only while the closed-keyboard layout applies (no inline height yet) — a repeat
      // keyboardDidShow (e.g. focus moving between inputs) would otherwise measure the resized
      // chat against the tab bar hidden behind the keyboard. `.nav__links` is the fixed bottom
      // tab bar on mobile.
      if (closedGap === undefined && !el.style.height) {
        const nav = document.querySelector('.nav__links') as HTMLElement | null
        closedGap = nav ? Math.max(0, nav.getBoundingClientRect().top - rect.bottom) : 10
      }
      // Nothing in the webview resizes when the iOS keyboard opens (body, 100vh and
      // visualViewport all keep the full screen height — measured), so the keyboard height from
      // the plugin event is the only usable signal: end the chat above the keyboard with the
      // same gap the closed layout leaves above the bottom tab bar.
      el.style.height = `${window.innerHeight - keyboardHeight - rect.top - (closedGap ?? 10)}px`
    }
    const resetChatHeight = () => {
      const el = chatEl()
      if (el) el.style.height = ''
    }

    const add = (promise: Promise<PluginListenerHandle>) =>
      promise.then((l) => {
        if (removed) l.remove()
        else listeners.push(l)
      })

    add(Keyboard.addListener('keyboardWillHide', resetChatHeight))
    add(
      Keyboard.addListener('keyboardDidShow', (info) => {
        fitChatAboveKeyboard(info.keyboardHeight)

        const chatList = document.getElementsByClassName('str-chat__list')
        if (chatList[0]) chatList[0].scrollTop = chatList[0].scrollHeight

        const chatModal = document.getElementsByClassName('str-chat__modal--open') as HTMLCollectionOf<HTMLElement>
        if (chatModal[0]) chatModal[0].style.paddingBottom = info.keyboardHeight - 81 + 'px'
      }),
    )

    return () => {
      removed = true
      listeners.forEach((l) => l.remove())
      resetChatHeight()
    }
  }, [])

  useEffect(() => {
    // @ts-ignore // name exists on channel data
    const channelName: string | undefined = currentChannel?.data?.name
    const channelId = currentChannel?.id
    if (channelId && channelName) clearChannelPushNotifications(channelId, channelName)

    let appListener: PluginListenerHandle | undefined
    let removed = false
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && channelId && channelName) clearChannelPushNotifications(channelId, channelName)
    }).then((l) => {
      if (removed) l.remove()
      else appListener = l
    })
    return () => {
      removed = true
      appListener?.remove()
    }
  }, [currentChannel])

  useEffect(() => {
    setError(false)
    const requestId = ++requestIdRef.current
    const isStale = () => requestId !== requestIdRef.current

    if (!kind || !id) {
      setCurrentChannel(undefined)
      return
    }

    const openChannel = async () => {
      try {
        if (kind === 'challenge') {
          await api.activity.joinChat({ goalId: id })
        } else {
          await api.activity.joinGroupChat({ divisionId: id })
        }
        if (isStale()) return

        const client = await getChatClient(userId, name)
        if (!client) throw new Error('getChatClient failed')
        if (isStale()) return

        const channel = client.channel(kind === 'challenge' ? channelType : groupChannelType, id)
        await channel.watch(kind === 'challenge' ? { messages: { limit: 10 } } : undefined)
        if (isStale()) return

        setChatClient(client)
        setCurrentChannel(channel)
      } catch (e: any) {
        console.error('useChatChannel error: ', e)
        Bugsnag.notify(e)
        if (!isStale()) {
          setCurrentChannel(undefined)
          setError(true)
        }
      }
    }

    openChannel()
  }, [kind, id])

  return { chatClient, currentChannel, error }
}
